/**
 * Hash Generator View
 * Interactive hash generation with algorithm selection
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import TextInput from 'ink-text-input';
import { UtilsService, HashAlgorithm } from '../../../services/utils.service.js';

const ALGORITHMS: HashAlgorithm[] = ['sha256', 'sha512', 'sha1', 'md5', 'bcrypt'];

export const HashView: React.FC = () => {
    const [text, setText] = useState('hello world');
    const [algorithm, setAlgorithm] = useState<HashAlgorithm>('sha256');
    const [hash, setHash] = useState('');
    const [focusedField, setFocusedField] = useState<'text' | 'algorithm' | 'generate'>('text');
    const [copied, setCopied] = useState(false);

    const service = new UtilsService();

    // Auto-generate on first mount
    React.useEffect(() => {
        handleGenerate();
    }, []);

    useInput((input, key) => {
        if (key.tab) {
            const fields: typeof focusedField[] = ['text', 'algorithm', 'generate'];
            const currentIndex = fields.indexOf(focusedField);
            setFocusedField(fields[(currentIndex + 1) % fields.length]);
        } else if (key.return) {
            // Generate on Enter from any field
            handleGenerate();
        } else if (key.leftArrow && focusedField === 'algorithm') {
            const currentIndex = ALGORITHMS.indexOf(algorithm);
            setAlgorithm(ALGORITHMS[currentIndex > 0 ? currentIndex - 1 : ALGORITHMS.length - 1]);
        } else if (key.rightArrow && focusedField === 'algorithm') {
            const currentIndex = ALGORITHMS.indexOf(algorithm);
            setAlgorithm(ALGORITHMS[(currentIndex + 1) % ALGORITHMS.length]);
        } else if (input === 'c' && hash) {
            handleCopy();
        }
    });

    const handleGenerate = async () => {
        if (!text) return;

        let result: string;
        if (algorithm === 'bcrypt') {
            result = await service.hashBcrypt(text, 10);
        } else {
            result = await service.hash(text, algorithm);
        }
        setHash(result);
        setCopied(false);
    };

    const handleCopy = async () => {
        try {
            const { default: clipboardy } = await import('clipboardy');
            await clipboardy.write(hash);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard not available
        }
    };

    return (
        <Box flexDirection="column">
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    🔒 Hash Generator
                </Text>
            </Box>

            <Box
                flexDirection="column"
                borderStyle="single"
                borderColor="gray"
                paddingX={2}
                paddingY={1}
                marginBottom={1}
            >
                <Text bold color="yellow" marginBottom={1}>
                    Options:
                </Text>

                <Box marginBottom={1} flexDirection="column">
                    <Box marginBottom={0}>
                        <Text color={focusedField === 'text' ? 'green' : undefined}>
                            {focusedField === 'text' ? '▶ ' : '  '}Text to hash:
                        </Text>
                    </Box>
                    <Box marginLeft={2} width={60}>
                        {focusedField === 'text' ? (
                            <TextInput value={text} onChange={setText} placeholder="Enter text..." />
                        ) : (
                            <Text dimColor={!text}>{text || '(empty)'}</Text>
                        )}
                    </Box>
                </Box>

                <Box marginBottom={1}>
                    <Box width={20}>
                        <Text color={focusedField === 'algorithm' ? 'green' : undefined}>
                            {focusedField === 'algorithm' ? '▶ ' : '  '}Algorithm:
                        </Text>
                    </Box>
                    <Text bold color={focusedField === 'algorithm' ? 'yellow' : undefined}>
                        {algorithm.toUpperCase()}
                    </Text>
                    {focusedField === 'algorithm' && (
                        <Text dimColor> (← → to change)</Text>
                    )}
                </Box>

                <Box marginTop={1}>
                    <Text
                        bold
                        backgroundColor={focusedField === 'generate' ? 'green' : undefined}
                        color={focusedField === 'generate' ? 'black' : 'green'}
                    >
                        {focusedField === 'generate' ? '▶ ' : '  '}
                        [ Generate Hash ]
                    </Text>
                </Box>
            </Box>

            {hash && (
                <Box
                    flexDirection="column"
                    borderStyle="single"
                    borderColor="green"
                    paddingX={2}
                    paddingY={1}
                >
                    <Box marginBottom={1}>
                        <Text bold color="green">
                            ✓ Hash Result:
                        </Text>
                        {copied && (
                            <Box marginLeft={2}>
                                <Text color="green">✓ Copied to clipboard!</Text>
                            </Box>
                        )}
                    </Box>

                    <Box flexDirection="column">
                        <Text dimColor>Algorithm: {algorithm.toUpperCase()}</Text>
                        <Box marginTop={1}>
                            <Text wrap="wrap">{hash}</Text>
                        </Box>
                    </Box>
                </Box>
            )}

            <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={2}>
                <Text dimColor>
                    Tab: Next | ←/→: Change algorithm | Enter: Generate | C: Copy | Esc: Back
                </Text>
            </Box>
        </Box>
    );
};
